import React, { useState, useEffect, useRef } from 'react';
import { getChats, getChatMessages, sendMessage as sendMessageAPI, finalizarChat, uploadFiles, atribuirAgente, removerAgente, transferirChat, getUsuariosDisponiveis, createNewChatAPI, getGroups, markChatAsRead } from '../services/api';
import MensagensRapidas from '../components/MensagensRapidas';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import { 
  MessageCircle, 
  Phone, 
  Mail, 
  Clock, 
  User, 
  Search, 
  Filter, 
  MoreVertical, 
  Send, 
  Paperclip, 
  Smile, 
  Mic, 
  Video, 
  PhoneCall,
  CheckCircle,
  AlertCircle,
  XCircle,
  Star,
  Tag,
  Calendar,
  MapPin,
  CreditCard,
  FileText,
  File,
  Image as ImageIcon,
  Film,
  Download,
  Archive,
  Trash2,
  Users,
  Activity,
  TrendingUp,
  Bell,
  Settings,
  Plus,
  ChevronDown,
  ChevronUp,
  Minimize2,
  Maximize2,
  Zap,
  Shield,
  Award,
  Play,
  Pause,
  RefreshCw,
  UserX,
  ArrowRightLeft,
  Eye
} from 'lucide-react';
import './Atendimento.css';

const computeChatBloqueado = (messages) => {
  if (!messages?.length) return false;
  const primeiraMensagem = messages[0];
  const isNovaConversa =
    primeiraMensagem &&
    primeiraMensagem.type === 'agent' &&
    primeiraMensagem.content &&
    primeiraMensagem.content.toLowerCase().includes('nova conversa iniciada');
  const clienteRespondeu = messages.some(
    (msg) => msg.type === 'customer' && msg.content && msg.content.trim().length > 0
  );
  return isNovaConversa && !clienteRespondeu;
};

const Atendimento = () => {
  const { user } = useAuth();
  const messagesCacheRef = useRef(new Map());
  const [selectedChat, setSelectedChat] = useState(null);
  const [isSpying, setIsSpying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [newMessage, setNewMessage] = useState('');
  const [chats, setChats] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [novoAgente, setNovoAgente] = useState('');
  const [usuariosDisponiveis, setUsuariosDisponiveis] = useState([]);
  const [gruposDisponiveis, setGruposDisponiveis] = useState([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState('');

  // Função para selecionar chat e atribuir agente
  const handleSelectChat = async (chat) => {
    try {
      // Atribuir o agente (nome completo do usuário) ao chat
      const agenteNome = user?.full_name || user?.username || 'Agente';
      await atribuirAgente(chat.id, agenteNome);
      
      // Atualizar o chat na lista local (zera não lidas ao abrir)
      setChats(prevChats => 
        prevChats.map(c => 
          c.id === chat.id ? { ...c, agent: agenteNome, unread_count: 0 } : c
        )
      );
      
      // Selecionar o chat
      setSelectedChat({ ...chat, agent: agenteNome, unread_count: 0 });
      setIsSpying(false);
    } catch (error) {
      console.error('Erro ao atribuir agente:', error);
      // Mesmo com erro, selecionar o chat
      setChats(prevChats =>
        prevChats.map(c => (c.id === chat.id ? { ...c, unread_count: 0 } : c))
      );
      setSelectedChat({ ...chat, unread_count: 0 });
      setIsSpying(false);
    }
  };

  // Janela para considerar online (minutos)
  const ONLINE_WINDOW_MINUTES = 5;

  // Função para carregar usuários disponíveis (apenas online)
  const loadUsuariosDisponiveis = async () => {
    try {
      const response = await getUsuariosDisponiveis(ONLINE_WINDOW_MINUTES);
      setUsuariosDisponiveis(response.usuarios || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  // Função para carregar grupos disponíveis
  const loadGruposDisponiveis = async () => {
    try {
      const response = await getGroups();
      setGruposDisponiveis(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  // Função para abrir modal de transferência
  const handleOpenTransferModal = async () => {
    setShowTransferModal(true);
    await loadUsuariosDisponiveis();
  };

  // Função para transferir chat
  const handleTransferChat = async () => {
    if (!selectedChat || !novoAgente.trim()) return;

    try {
      await transferirChat(selectedChat.id, novoAgente.trim());
      
      // Atualizar o chat na lista local
      setChats(prevChats => 
        prevChats.map(c => 
          c.id === selectedChat.id ? { ...c, agent: novoAgente.trim() } : c
        )
      );
      
      // Atualizar o chat selecionado
      setSelectedChat({ ...selectedChat, agent: novoAgente.trim() });
      setShowTransferModal(false);
      setNovoAgente('');
    } catch (error) {
      console.error('Erro ao transferir chat:', error);
    }
  };

  // Função para abrir modal de remover agente
  const handleOpenRemoveModal = async () => {
    setShowRemoveModal(true);
    setGrupoSelecionado(selectedChat?.grupo || ''); // Preencher com grupo atual se houver
    await loadGruposDisponiveis();
  };

  // Função para remover agente (colocar na fila)
  const handleRemoveAgent = async () => {
    if (!selectedChat) return;

    try {
      // Enviar grupo selecionado (ou string vazia se não selecionado)
      const grupo = grupoSelecionado && grupoSelecionado.trim() ? grupoSelecionado.trim() : '';
      await removerAgente(selectedChat.id, grupo || null);
      
      // Atualizar o chat na lista local
      const novoGrupo = grupo ? grupo : null;
      setChats(prevChats => 
        prevChats.map(c => 
          c.id === selectedChat.id ? { ...c, agent: '', grupo: novoGrupo } : c
        )
      );
      
      // Atualizar o chat selecionado
      setSelectedChat({ ...selectedChat, agent: '', grupo: novoGrupo });
      setShowRemoveModal(false);
      setGrupoSelecionado('');
    } catch (error) {
      console.error('Erro ao remover agente:', error);
    }
  };

  // Espiar (admin) sem atribuir
  const handleSpyChat = async (chat) => {
    if (!(user?.is_admin || user?.is_group_admin)) return;
    try {
      setIsSpying(true);
      const response = await getChatMessages(chat.protocolo);
      const msgs = response.messages || [];
      messagesCacheRef.current.set(chat.id, msgs);
      setChats((prevChats) =>
        prevChats.map((c) => (c.id === chat.id ? { ...c, unread_count: 0 } : c))
      );
      setSelectedChat({
        ...chat,
        messages: msgs,
        unread_count: 0
      });
      const lastId = msgs[msgs.length - 1]?.id;
      if (lastId) {
        markChatAsRead(chat.protocolo, lastId).catch(() => {});
      }
    } catch (error) {
      console.error('Erro ao espiar chat:', error);
      setIsSpying(false);
      alert('Não foi possível carregar a conversa.');
    }
  };

  const exitSpy = () => {
    setIsSpying(false);
  };

  // Calcular estatísticas reais baseadas nos dados
  const activeAgents = chats.filter(chat => chat.agent && chat.agent.trim() !== '').length;
  const queueLength = chats.filter(chat => chat.status === 'pendente' || chat.status === 'ativo').length;
  
  // Calcular tempo médio de resposta baseado nas mensagens reais
  const calculateAvgResponseTime = () => {
    if (!chats.length) return '0m 0s';
    
    let totalResponseTime = 0;
    let responseCount = 0;
    
    chats.forEach(chat => {
      if (chat.messages && chat.messages.length > 1) {
        const messages = chat.messages;
        for (let i = 1; i < messages.length; i++) {
          if (messages[i-1].type === 'customer' && messages[i].type === 'agent') {
            // Calcular tempo entre mensagem do cliente e resposta do agente
            const customerTime = new Date(messages[i-1].timestamp);
            const agentTime = new Date(messages[i].timestamp);
            const responseTime = agentTime - customerTime;
            
            if (responseTime > 0 && responseTime < 3600000) { // Menos de 1 hora
              totalResponseTime += responseTime;
              responseCount++;
            }
          }
        }
      }
    });
    
    if (responseCount === 0) return '0m 0s';
    
    const avgMs = totalResponseTime / responseCount;
    const minutes = Math.floor(avgMs / 60000);
    const seconds = Math.floor((avgMs % 60000) / 1000);
    
    return `${minutes}m ${seconds}s`;
  };
  
  const avgResponseTime = calculateAvgResponseTime();
  
  // Calcular satisfação baseada em chats finalizados com sucesso
  const calculateSatisfaction = () => {
    if (!chats.length) return '0%';
    
    const totalChats = chats.length;
    const successfulChats = chats.filter(chat => 
      chat.status === 'finalizado' && 
      chat.messages && 
      chat.messages.length > 0
    ).length;
    
    const satisfaction = Math.round((successfulChats / totalChats) * 100);
    return `${satisfaction}%`;
  };
  
  const satisfaction = calculateSatisfaction();
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatData, setNewChatData] = useState({
    customerName: '',
    phone: '',
    email: '',
    channel: 'whatsapp',
    priority: 'média'
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [lastActivity, setLastActivity] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [chatBloqueado, setChatBloqueado] = useState(false);
  const selectedChatIdRef = useRef(null);

  useEffect(() => {
    selectedChatIdRef.current = selectedChat?.id ?? null;
  }, [selectedChat?.id]);

  const markMessagesAsRead = async (protocolo, messages, chatId) => {
    if (!protocolo || !messages?.length) return;
    const lastId = messages[messages.length - 1]?.id;
    if (!lastId) return;
    setChats((prevChats) =>
      prevChats.map((c) => (c.id === chatId ? { ...c, unread_count: 0 } : c))
    );
    try {
      await markChatAsRead(protocolo, lastId);
    } catch (error) {
      console.error('Erro ao marcar mensagens como lidas:', error);
    }
  };

  // Carregar chats do banco de dados
  useEffect(() => {
    const loadChats = async () => {
    try {
      setLoading(true);
      console.log('Carregando chats do banco de dados...');
      const response = await getChats(10, false); // departamento_id = 10, finalizada = false
      console.log('Chats carregados:', response);
      // Backend já filtra por grupos, então usar diretamente os chats retornados
      setChats(response.chats || []);
        
        // Não selecionar automaticamente o primeiro chat - deixar o usuário escolher
      } catch (error) {
        console.error('Erro ao carregar chats:', error);
        alert('Erro ao carregar chats do banco de dados. Verifique a conexão.');
        // Não usar dados mockup, deixar vazio
        setChats([]);
      } finally {
        setLoading(false);
      }
    };

    loadChats();
  }, []);

  useEffect(() => {
    if (!selectedChat?.id) return;
    const msgs = selectedChat.messages;
    if (Array.isArray(msgs)) {
      messagesCacheRef.current.set(selectedChat.id, msgs);
    }
  }, [selectedChat?.id, selectedChat?.messages]);

  // Carregar mensagens ao selecionar chat — instantâneo se já houver cache (volta à conversa)
  useEffect(() => {
    let active = true;

    const loadMessages = async () => {
      if (!selectedChat || !selectedChat.protocolo) {
        console.log('Chat não selecionado ou sem protocolo:', selectedChat);
        return;
      }

      const chatId = selectedChat.id;
      const protocolo = selectedChat.protocolo;
      const cached = messagesCacheRef.current.has(chatId)
        ? messagesCacheRef.current.get(chatId)
        : null;

      if (cached) {
        const deveBloquear = computeChatBloqueado(cached);
        setChatBloqueado(deveBloquear);
        setSelectedChat((prev) => (prev && prev.id === chatId ? { ...prev, messages: cached, unread_count: 0 } : prev));
        setChats((prevChats) =>
          prevChats.map((chat) => (chat.id === chatId ? { ...chat, messages: cached, unread_count: 0 } : chat))
        );
        setLoadingMessages(false);
        markMessagesAsRead(protocolo, cached, chatId);
      } else {
        setLoadingMessages(true);
      }

      try {
        const response = await getChatMessages(protocolo);
        if (!active) return;

        const messages = response.messages || [];
        const deveBloquear = computeChatBloqueado(messages);
        setChatBloqueado(deveBloquear);

        if (deveBloquear) {
          console.log('🔒 Chat bloqueado - Aguardando confirmação do cliente');
        }

        messagesCacheRef.current.set(chatId, messages);

        setSelectedChat((prevChat) =>
          prevChat && prevChat.id === chatId ? { ...prevChat, messages, unread_count: 0 } : prevChat
        );

        setChats((prevChats) =>
          prevChats.map((chat) => (chat.id === chatId ? { ...chat, messages, unread_count: 0 } : chat))
        );

        await markMessagesAsRead(protocolo, messages, chatId);
      } catch (error) {
        console.error('Erro ao carregar mensagens:', error);
        if (!active) return;
        if (!cached) {
          alert('Erro ao carregar mensagens do chat.');
        }
      } finally {
        if (active) {
          setLoadingMessages(false);
        }
      }
    };

    loadMessages();
    return () => {
      active = false;
    };
  }, [selectedChat?.id]);

  // Auto-atualização da lista de chats (sempre ativa)
  useEffect(() => {
    let interval;
    
    const updateChats = async () => {
      // Verificar se a página está visível (Page Visibility API)
      if (document.hidden) {
        return; // Não atualizar se a aba não está visível
      }
      
      try {
        const response = await getChats(10, false);
        if (response.chats) {
          const selectedId = selectedChatIdRef.current;
          setChats((prevChats) => {
            const prevById = new Map(prevChats.map((c) => [c.id, c]));
            return response.chats.map((chat) => {
              const prev = prevById.get(chat.id);
              return {
                ...chat,
                unread_count: chat.id === selectedId ? 0 : (chat.unread_count || 0),
                messages: prev?.messages,
              };
            });
          });
        }
      } catch (error) {
        console.error('Erro ao atualizar lista de chats:', error);
      }
    };
    
    // Atualizar imediatamente
    updateChats();
    
    // Configurar intervalo (aumentado para 15 segundos)
    interval = setInterval(updateChats, 15000);
    
    // Atualizar quando a página voltar a ficar visível
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateChats();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Auto-atualização inteligente das mensagens (OTIMIZADO)
  useEffect(() => {
    if (!selectedChat?.protocolo) return;

    let interval;
    let lastMessageCount = selectedChat?.messages?.length || 0;
    let lastMessageId = selectedChat?.messages?.[selectedChat.messages.length - 1]?.id || 0;

    const updateMessages = async () => {
      // Verificar se a página está visível
      if (document.hidden) {
        return;
      }
      
      try {
        const response = await getChatMessages(selectedChat.protocolo);
        
        if (response.messages && response.messages.length > 0) {
          const currentMessages = selectedChat.messages || [];
          const newMessages = response.messages;
          
          // OTIMIZAÇÃO: Comparar apenas count e último ID em vez de JSON.stringify
          const hasNewMessages = newMessages.length !== lastMessageCount;
          const newLastMessageId = newMessages[newMessages.length - 1]?.id || 0;
          const messagesChanged = hasNewMessages || newLastMessageId !== lastMessageId;
          
          if (messagesChanged) {
            const deveBloquear = computeChatBloqueado(newMessages);

            if (chatBloqueado !== deveBloquear) {
              setChatBloqueado(deveBloquear);
              if (!deveBloquear && chatBloqueado) {
                console.log('✅ Chat desbloqueado - Cliente respondeu!');
              }
            }
            
            // Atualizar contadores
            lastMessageCount = newMessages.length;
            lastMessageId = newLastMessageId;
            
            console.log(`📨 Novas mensagens detectadas: ${newMessages.length - currentMessages.length}`);
            
            const lastType = newMessages[newMessages.length - 1]?.type;
            const needsReply = lastType === 'customer';

            // Atualizar mensagens do chat selecionado
            setSelectedChat(prevChat => ({
              ...prevChat,
              messages: newMessages,
              unread_count: 0,
              awaiting_reply: needsReply,
            }));
            
            // Atualizar também na lista de chats
            setChats(prevChats => 
              prevChats.map(chat => 
                chat.id === selectedChat.id 
                  ? { ...chat, messages: newMessages, unread_count: 0, awaiting_reply: needsReply }
                  : chat
              )
            );

            // Chat aberto: manter como lido
            markMessagesAsRead(selectedChat.protocolo, newMessages, selectedChat.id);
          }
        }
      } catch (error) {
        console.error('Erro na auto-atualização:', error);
      }
    };

    // Primeira carga vem do efeito loadMessages; evita 2× GET ao trocar de chat
    interval = setInterval(updateMessages, 5000);
    
    // Atualizar quando a página voltar a ficar visível
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateMessages();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedChat?.protocolo, selectedChat?.id, chatBloqueado]);

  // OTIMIZAÇÃO: Memoizar filteredChats para evitar recálculo desnecessário
  const chatNeedsReply = (chat) => {
    if (!chat) return false;
    if (typeof chat.awaiting_reply === 'boolean') return chat.awaiting_reply;
    const msgs = chat.messages;
    if (Array.isArray(msgs) && msgs.length > 0) {
      return msgs[msgs.length - 1]?.type === 'customer';
    }
    return (chat.unread_count || 0) > 0;
  };

  const awaitingReplyCount = React.useMemo(
    () => (chats || []).filter((c) => chatNeedsReply(c)).length,
    [chats]
  );

  const filteredChats = React.useMemo(() => {
    if (!chats || chats.length === 0) return [];
    
    return chats.filter(chat => {
      const customerName = chat.customer_name || (chat.customer && chat.customer.name) || 'Cliente';
      const phone = chat.phone || (chat.customer && chat.customer.phone) || '';
      const lastMessage = chat.last_message || chat.lastMessage || '';
      
      const matchesSearch = !searchTerm || 
                           customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           phone.includes(searchTerm) ||
                           lastMessage.toLowerCase().includes(searchTerm.toLowerCase());
      
      let matchesFilter = true;
      if (filterStatus === 'falta_responder') {
        matchesFilter = chatNeedsReply(chat);
      } else if (filterStatus !== 'todos') {
        matchesFilter = chat.status === filterStatus;
      }
      
      return matchesSearch && matchesFilter;
    });
  }, [chats, searchTerm, filterStatus]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativo': return 'atendimento-status-active';
      case 'pendente': return 'atendimento-status-pending';
      case 'resolvido': return 'atendimento-status-resolved';
      default: return 'atendimento-status-default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'alta': return 'atendimento-priority-high';
      case 'média': return 'atendimento-priority-medium';
      case 'baixa': return 'atendimento-priority-low';
      default: return 'atendimento-priority-default';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp': return <MessageCircle size={16} />;
      case 'telefone': return <Phone size={16} />;
      case 'email': return <Mail size={16} />;
      default: return <MessageCircle size={16} />;
    }
  };

  // Função para formatar texto do WhatsApp (negrito, itálico, etc.)
  const formatMessageText = (text) => {
    if (!text) return '';
    
    // Escapar HTML para segurança primeiro
    const escapeHtml = (str) => {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return str.replace(/[&<>"']/g, m => map[m]);
    };
    
    let escaped = escapeHtml(text);
    
    // Processar formatação do WhatsApp em ordem
    let formatted = escaped
      // Quebras de linha primeiro - preservar múltiplas quebras
      .split('\n')
      .map((line, index, array) => {
        // Negrito: *texto* (pode ter múltiplos na mesma linha)
        line = line.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');
        // Itálico: _texto_ (após negrito)
        line = line.replace(/_([^_\n]+)_/g, '<em>$1</em>');
        // Riscado: ~texto~
        line = line.replace(/~([^~\n]+)~/g, '<del>$1</del>');
        return line;
      })
      .join('<br />');
    
    return formatted;
  };

  const sendMessage = async () => {
    if (isSpying) return; // Admin em visualização não envia
    if (!newMessage.trim() || !selectedChat) return;
    
    // Verificar se o chat está bloqueado (aguardando confirmação do cliente)
    if (chatBloqueado) {
      alert('⏳ Chat bloqueado! Aguarde a confirmação do cliente antes de enviar mensagens.');
      return;
    }

    // Registrar atividade do usuário
    setLastActivity(Date.now());

    const messageContent = newMessage;
    setNewMessage(''); // Limpar campo imediatamente

    // Adicionar mensagem imediatamente na interface (otimista)
    const newMsg = {
      id: Date.now(),
      type: 'agent',
      content: messageContent,
      timestamp: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      status: 'sending' // Status de enviando
    };

    // Atualizar interface imediatamente
    setChats(prevChats => 
      prevChats.map(chat => 
        chat.id === selectedChat.id 
          ? { 
              ...chat, 
              messages: [...chat.messages, newMsg],
              lastMessage: messageContent,
              lastMessageTime: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
              awaiting_reply: false,
            }
          : chat
      )
    );

    setSelectedChat(prevChat => ({
      ...prevChat,
      messages: [...prevChat.messages, newMsg],
      lastMessage: messageContent,
      lastMessageTime: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      awaiting_reply: false,
    }));

    // Enviar para backend em background
    try {
      await sendMessageAPI(selectedChat.protocolo, messageContent);
      
      // Atualizar status para enviado
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.id === selectedChat.id 
            ? { 
                ...chat, 
                messages: chat.messages.map(msg => 
                  msg.id === newMsg.id ? { ...msg, status: 'sent' } : msg
                )
              }
            : chat
        )
      );

      setSelectedChat(prevChat => ({
        ...prevChat,
        messages: prevChat.messages.map(msg => 
          msg.id === newMsg.id ? { ...msg, status: 'sent' } : msg
        )
      }));
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      // Atualizar status para erro
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.id === selectedChat.id 
            ? { 
                ...chat, 
                messages: chat.messages.map(msg => 
                  msg.id === newMsg.id ? { ...msg, status: 'error' } : msg
                )
              }
            : chat
        )
      );

      setSelectedChat(prevChat => ({
        ...prevChat,
        messages: prevChat.messages.map(msg => 
          msg.id === newMsg.id ? { ...msg, status: 'error' } : msg
        )
      }));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const createNewChat = async () => {
    if (!newChatData.customerName.trim() || !newChatData.phone.trim()) {
      alert('Por favor, preencha o nome e telefone do cliente.');
      return;
    }

    // Limpar telefone (remover espaços, caracteres especiais)
    const phoneClean = newChatData.phone.replace(/\D/g, '');
    
    if (phoneClean.length < 10) {
      alert('Por favor, informe um telefone válido com pelo menos 10 dígitos.');
      return;
    }

    try {
      // Chamar o endpoint do backend para criar a conversa
      const response = await createNewChatAPI({
        customer_name: newChatData.customerName.trim(),
        phone: phoneClean
      });

      if (response.success) {
        // Recarregar a lista de chats para incluir a nova conversa
        const chatsResponse = await getChats(10, false);
        if (chatsResponse.chats) {
          const allChats = chatsResponse.chats;
          const currentUserName = (user?.full_name || user?.username || '').trim();
          const filtered = (user?.is_admin || user?.is_group_admin)
            ? allChats
            : allChats.filter(c => {
                const agent = (c.agent || '').trim();
                return agent === '' || agent === currentUserName;
              });
          setChats(filtered);
          
          // Selecionar o novo chat
          const newChat = allChats.find(c => c.protocolo === response.protocolo);
          if (newChat) {
            setSelectedChat(newChat);
          }
        }
        
        setShowNewChatModal(false);
        setNewChatData({
          customerName: '',
          phone: '',
          email: '',
          channel: 'whatsapp',
          priority: 'média'
        });
        
        alert('Nova conversa criada com sucesso! Template do WhatsApp enviado.');
      } else {
        alert('Erro ao criar conversa: ' + (response.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao criar nova conversa:', error);
      alert('Erro ao criar conversa: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleNewChatInputChange = (field, value) => {
    setNewChatData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Função para adicionar emoji
  const addEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Função para selecionar arquivos
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  // Função para processar arquivos arrastados
  const handleDropFiles = (files) => {
    const fileArray = Array.from(files);
    setSelectedFiles(prev => [...prev, ...fileArray]);
    setIsDragging(false);
  };

  // Handlers para drag and drop
  const dragCounter = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleDropFiles(e.dataTransfer.files);
    }
  };

  // Função para remover arquivo
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Função para iniciar gravação
  const startRecording = () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          const mediaRecorder = new MediaRecorder(stream);
          const chunks = [];
          
          mediaRecorder.ondataavailable = (event) => {
            chunks.push(event.data);
          };
          
          mediaRecorder.onstop = () => {
            const blob = new Blob(chunks, { type: 'audio/wav' });
            setAudioBlob(blob);
            stream.getTracks().forEach(track => track.stop());
          };
          
          mediaRecorder.start();
          setIsRecording(true);
          setRecordingTime(0);
          
          const timer = setInterval(() => {
            setRecordingTime(prev => prev + 1);
          }, 1000);
          
          // Parar gravação após 60 segundos
          setTimeout(() => {
            mediaRecorder.stop();
            setIsRecording(false);
            clearInterval(timer);
          }, 60000);
          
          // Armazenar referências para parar
          window.currentMediaRecorder = mediaRecorder;
          window.currentTimer = timer;
        })
        .catch(err => {
          console.error('Erro ao acessar microfone:', err);
          alert('Erro ao acessar o microfone. Verifique as permissões.');
        });
    } else {
      alert('Gravação de áudio não suportada neste navegador.');
    }
  };

  // Função para parar gravação
  const stopRecording = () => {
    if (window.currentMediaRecorder) {
      window.currentMediaRecorder.stop();
    }
    if (window.currentTimer) {
      clearInterval(window.currentTimer);
    }
    setIsRecording(false);
  };

  // Função para enviar áudio
  const sendAudio = async () => {
    if (!audioBlob || !selectedChat) return;

    // Registrar atividade do usuário
    setLastActivity(Date.now());

    const audioUrl = URL.createObjectURL(audioBlob);
    const audioMessage = {
      id: Date.now(),
      type: 'agent',
      content: '🎤 Mensagem de áudio',
      timestamp: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      status: 'sending',
      audioUrl: audioUrl,
      isAudio: true,
      duration: recordingTime
    };

    // Adicionar mensagem imediatamente na interface (otimista)
    setChats(prevChats => 
      prevChats.map(chat => 
        chat.id === selectedChat.id 
          ? { 
              ...chat, 
              messages: [...chat.messages, audioMessage],
              lastMessage: '🎤 Mensagem de áudio',
              lastMessageTime: audioMessage.timestamp
            }
          : chat
      )
    );

    setSelectedChat(prevChat => ({
      ...prevChat,
      messages: [...prevChat.messages, audioMessage],
      lastMessage: '🎤 Mensagem de áudio',
      lastMessageTime: audioMessage.timestamp
    }));

    // Enviar áudio para o backend
    try {
      // Criar um arquivo com nome apropriado
      const audioFile = new File([audioBlob], `audio_${Date.now()}.ogg`, {
        type: 'audio/ogg'
      });
      
      const response = await uploadFiles(selectedChat.protocolo, [audioFile]);
      console.log('Áudio enviado:', response);
      
      // Atualizar status para enviado
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.id === selectedChat.id 
            ? { 
                ...chat, 
                messages: chat.messages.map(msg => 
                  msg.id === audioMessage.id ? { ...msg, status: 'sent' } : msg
                )
              }
            : chat
        )
      );

      setSelectedChat(prevChat => ({
        ...prevChat,
        messages: prevChat.messages.map(msg => 
          msg.id === audioMessage.id ? { ...msg, status: 'sent' } : msg
        )
      }));

    } catch (error) {
      console.error('Erro ao enviar áudio:', error);
      
      // Marcar mensagem como erro
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.id === selectedChat.id 
            ? { 
                ...chat, 
                messages: chat.messages.map(msg => 
                  msg.id === audioMessage.id ? { ...msg, status: 'error' } : msg
                )
              }
            : chat
        )
      );

      setSelectedChat(prevChat => ({
        ...prevChat,
        messages: prevChat.messages.map(msg => 
          msg.id === audioMessage.id ? { ...msg, status: 'error' } : msg
        )
      }));
    }

    setAudioBlob(null);
  };

  // Função para reproduzir áudio
  const playAudio = (audioUrl, messageId) => {
    if (playingAudio === messageId) {
      // Se já está tocando, para
      setPlayingAudio(null);
      return;
    }

    // Para qualquer áudio que esteja tocando
    setPlayingAudio(null);

    // Cria um novo elemento de áudio
    const audio = new Audio(audioUrl);
    
    audio.onloadedmetadata = () => {
      setAudioDuration(audio.duration);
    };

    audio.onplay = () => {
      setPlayingAudio(messageId);
    };

    audio.onended = () => {
      setPlayingAudio(null);
    };

    audio.onerror = () => {
      console.error('Erro ao reproduzir áudio');
      setPlayingAudio(null);
    };

    // Reproduz o áudio
    audio.play().catch(error => {
      console.error('Erro ao reproduzir áudio:', error);
      setPlayingAudio(null);
    });
  };

  // Função para formatar duração
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s<>"']+|\/uploads\/[^\s<>"']+/i;
  const AUDIO_EXTENSIONS = ['.ogg', '.mp3', '.wav', '.m4a', '.aac'];
  const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
  const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
  const DOC_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.odt', '.rtf'];
  const SHEET_EXTENSIONS = ['.xls', '.xlsx', '.csv', '.ods'];
  const ARCHIVE_EXTENSIONS = ['.zip', '.rar', '.7z', '.tar', '.gz'];
  const FILE_EXTENSIONS = [
    ...IMAGE_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
    ...DOC_EXTENSIONS,
    ...SHEET_EXTENSIONS,
    ...ARCHIVE_EXTENSIONS,
    '.ppt', '.pptx', '.json', '.xml', '.blob'
  ];
  const FILE_EXT_ALT =
    'pdf|docx?|xlsx?|pptx?|png|jpe?g|gif|webp|bmp|ogg|mp3|wav|m4a|aac|mp4|webm|mov|avi|mkv|zip|rar|7z|tar|gz|txt|csv|json|xml|blob|odt|ods|rtf';

  const extractUrlFromContent = (content) => {
    if (!content) return null;
    const text = String(content).trim();

    // URLs de /uploads/ podem ter espaços no nome do arquivo
    const uploadsWithExt = text.match(
      new RegExp(
        `((?:https?:\\/\\/|\\/)[^\\n]*?\\/uploads\\/[^\\n]*?\\.(?:${FILE_EXT_ALT}))`,
        'i'
      )
    );
    if (uploadsWithExt) return uploadsWithExt[1];

    const uploadsAny = text.match(/((?:https?:\/\/|\/)[^\n]*?\/uploads\/[^\n]+)/i);
    if (uploadsAny) return uploadsAny[1].trim();

    const genericWithExt = text.match(
      new RegExp(`(https?:\\/\\/[^\\n]+?\\.(?:${FILE_EXT_ALT}))(?![\\w])`, 'i')
    );
    if (genericWithExt) return genericWithExt[1];

    const match = text.match(URL_IN_TEXT_PATTERN);
    return match ? match[0] : null;
  };

  // Função para detectar se uma mensagem contém URL de áudio
  const isAudioUrl = (content) => {
    const url = extractUrlFromContent(content);
    if (!url) return false;
    const lower = url.toLowerCase();
    return AUDIO_EXTENSIONS.some((ext) => lower.includes(ext));
  };

  // Função para extrair URL de áudio da mensagem
  const extractAudioUrl = (content) => extractUrlFromContent(content);

  const getFileNameFromUrl = (url) => {
    if (!url) return 'arquivo';
    try {
      const path = url.split('?')[0];
      const raw = decodeURIComponent(path.split('/').pop() || 'arquivo');
      return raw || 'arquivo';
    } catch {
      return 'arquivo';
    }
  };

  const getFileExtension = (nameOrUrl) => {
    if (!nameOrUrl) return '';
    const clean = String(nameOrUrl).split('?')[0].toLowerCase();
    const match = clean.match(/(\.[a-z0-9]+)$/i);
    return match ? match[1] : '';
  };

  const isImageFile = (nameOrUrl) =>
    IMAGE_EXTENSIONS.includes(getFileExtension(nameOrUrl));

  const isAttachmentUrl = (content) => {
    if (!content || isAudioUrl(content)) return false;
    const url = extractUrlFromContent(content);
    if (!url) return false;
    const lower = url.toLowerCase();
    if (lower.includes('/uploads/')) return true;
    return FILE_EXTENSIONS.some((ext) => lower.includes(ext));
  };

  const parseAttachmentContent = (content) => {
    const url = extractUrlFromContent(content);
    if (!url) return null;
    const caption = String(content).replace(url, '').trim();
    const filename = getFileNameFromUrl(url);
    return { url, caption, filename, ext: getFileExtension(filename || url) };
  };

  const getAttachmentIcon = (ext) => {
    if (IMAGE_EXTENSIONS.includes(ext)) return ImageIcon;
    if (VIDEO_EXTENSIONS.includes(ext)) return Film;
    if (ARCHIVE_EXTENSIONS.includes(ext)) return Archive;
    if (SHEET_EXTENSIONS.includes(ext)) return FileText;
    if (DOC_EXTENSIONS.includes(ext)) return FileText;
    return File;
  };

  const getAttachmentLabel = (ext) => {
    if (!ext) return 'ARQUIVO';
    return ext.replace('.', '').toUpperCase();
  };

  // Função para corrigir URLs de arquivos para o backend de atendimento
  const fixFileUrl = (url) => {
    if (!url) return url;

    let fixed = url.trim();
    if (fixed.includes('localhost:7010')) {
      fixed = fixed
        .replace('http://localhost:7010', 'http://fastnotify.com.br:7010')
        .replace('https://fastnotify.com.br', 'http://fastnotify.com.br:7010');
    }

    const encodePath = (pathAfterUploads) =>
      pathAfterUploads
        .split('/')
        .map((part) => {
          try {
            return encodeURIComponent(decodeURIComponent(part));
          } catch {
            return encodeURIComponent(part);
          }
        })
        .join('/');

    // Preferir a API configurada para /uploads/...
    const uploadsMatch = fixed.match(/\/uploads\/(.+)$/i);
    if (uploadsMatch) {
      const base = (config.API_URL || '').replace(/\/$/, '');
      return `${base}/uploads/${encodePath(uploadsMatch[1])}`;
    }

    if (fixed.startsWith('/uploads/')) {
      const base = (config.API_URL || '').replace(/\/$/, '');
      return `${base}/uploads/${encodePath(fixed.slice('/uploads/'.length))}`;
    }

    return fixed;
  };

  const downloadAttachment = async (url, filename) => {
    const downloadUrl = fixFileUrl(url);
    const safeName = filename || getFileNameFromUrl(downloadUrl) || 'arquivo';

    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Erro ao baixar anexo:', error);
      // Fallback: abrir em nova aba
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderFileCard = ({ url, filename, size, mimeType }) => {
    const ext = getFileExtension(filename || url || mimeType);
    const Icon = getAttachmentIcon(ext);
    const resolvedUrl = url ? fixFileUrl(url) : null;
    const showImage = resolvedUrl && isImageFile(filename || url);

    if (showImage) {
      return (
        <div className="atendimento-image-attachment">
          <button
            type="button"
            className="atendimento-image-preview-btn"
            onClick={() => downloadAttachment(resolvedUrl, filename)}
            title="Baixar imagem"
          >
            <img
              src={resolvedUrl}
              alt={filename || 'Imagem'}
              className="atendimento-image-preview"
            />
          </button>
          <div className="atendimento-file-message atendimento-file-message-compact">
            <div className="atendimento-file-icon" data-ext={ext || 'img'}>
              <Icon size={22} />
            </div>
            <div className="atendimento-file-info">
              <span className="atendimento-file-name" title={filename}>{filename}</span>
              <span className="atendimento-file-size">{getAttachmentLabel(ext || '.img')}</span>
            </div>
            <button
              type="button"
              className="atendimento-download-button"
              onClick={() => downloadAttachment(resolvedUrl, filename)}
              title="Baixar"
            >
              <Download size={16} />
            </button>
          </div>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="atendimento-file-message atendimento-file-message-clickable"
        onClick={() => resolvedUrl && downloadAttachment(resolvedUrl, filename)}
        disabled={!resolvedUrl}
        title={resolvedUrl ? 'Baixar arquivo' : 'Arquivo indisponível'}
      >
        <div className="atendimento-file-icon" data-ext={ext || 'file'}>
          <Icon size={22} />
          <span className="atendimento-file-ext">{getAttachmentLabel(ext)}</span>
        </div>
        <div className="atendimento-file-info">
          <span className="atendimento-file-name" title={filename}>{filename || 'Arquivo'}</span>
          <span className="atendimento-file-size">
            {typeof size === 'number'
              ? `${(size / 1024).toFixed(1)} KB`
              : getAttachmentLabel(ext)}
          </span>
        </div>
        <span className="atendimento-download-button" aria-hidden="true">
          <Download size={16} />
        </span>
      </button>
    );
  };

  // Função para finalizar chat
  const handleFinalizarChat = async () => {
    if (!selectedChat) return;
    
    if (window.confirm('Tem certeza que deseja finalizar este chat?')) {
      try {
        await finalizarChat(selectedChat.id);
        
        // Remover o chat da lista
        setChats(prevChats => 
          prevChats.filter(chat => chat.id !== selectedChat.id)
        );
        
        // Limpar o chat selecionado
        setSelectedChat(null);
        
        alert('Chat finalizado com sucesso!');
      } catch (error) {
        console.error('Erro ao finalizar chat:', error);
        alert('Erro ao finalizar chat. Tente novamente.');
      }
    }
  };

  // Função para atualizar mensagens manualmente
  const refreshMessages = async () => {
    if (!selectedChat?.protocolo) return;
    
    try {
      setLoadingMessages(true);
      const response = await getChatMessages(selectedChat.protocolo);
      
      if (response.messages) {
        setSelectedChat(prevChat => ({
          ...prevChat,
          messages: response.messages
        }));
        
        setChats(prevChats => 
          prevChats.map(chat => 
            chat.id === selectedChat.id 
              ? { ...chat, messages: response.messages }
              : chat
          )
        );
      }
    } catch (error) {
      console.error('Erro ao atualizar mensagens:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Função para enviar arquivos
  const sendFiles = async () => {
    if (selectedFiles.length === 0 || !selectedChat) return;

    // Registrar atividade do usuário
    setLastActivity(Date.now());

    const fileMessage = {
      id: Date.now(),
      type: 'agent',
      content: `📎 ${selectedFiles.length} arquivo(s) anexado(s)`,
      timestamp: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      status: 'sending',
      files: selectedFiles.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type
      })),
      isFile: true
    };

    // Adicionar mensagem imediatamente na interface (otimista)
    setChats(prevChats => 
      prevChats.map(chat => 
        chat.id === selectedChat.id 
          ? { 
              ...chat, 
              messages: [...chat.messages, fileMessage],
              lastMessage: `📎 ${selectedFiles.length} arquivo(s)`,
              lastMessageTime: fileMessage.timestamp
            }
          : chat
      )
    );

    setSelectedChat(prevChat => ({
      ...prevChat,
      messages: [...prevChat.messages, fileMessage],
      lastMessage: `📎 ${selectedFiles.length} arquivo(s)`,
      lastMessageTime: fileMessage.timestamp
    }));

    // Enviar arquivos reais para o backend
    try {
      const response = await uploadFiles(selectedChat.protocolo, selectedFiles);
      console.log('Arquivos enviados:', response);

      const protocolo = selectedChat.protocolo;
      const apiBase = (config.API_URL || '').replace(/\/$/, '');
      const filesWithUrl = (response.files || []).map((file) => ({
        name: file.filename,
        size: file.size,
        type: file.type || '',
        url: `${apiBase}/uploads/chat_${protocolo}/${encodeURIComponent(file.filename)}`
      }));
      
      // Atualizar status para enviado e anexar URLs para download
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.id === selectedChat.id 
            ? { 
                ...chat, 
                messages: chat.messages.map(msg => 
                  msg.id === fileMessage.id
                    ? { ...msg, status: 'sent', files: filesWithUrl.length ? filesWithUrl : msg.files }
                    : msg
                )
              }
            : chat
        )
      );

      setSelectedChat(prevChat => ({
        ...prevChat,
        messages: prevChat.messages.map(msg => 
          msg.id === fileMessage.id
            ? { ...msg, status: 'sent', files: filesWithUrl.length ? filesWithUrl : msg.files }
            : msg
        )
      }));

    } catch (error) {
      console.error('Erro ao enviar arquivos:', error);
      
      // Marcar mensagem como erro
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.id === selectedChat.id 
            ? { 
                ...chat, 
                messages: chat.messages.map(msg => 
                  msg.id === fileMessage.id ? { ...msg, status: 'error' } : msg
                )
              }
            : chat
        )
      );

      setSelectedChat(prevChat => ({
        ...prevChat,
        messages: prevChat.messages.map(msg => 
          msg.id === fileMessage.id ? { ...msg, status: 'error' } : msg
        )
      }));
    }

    setSelectedFiles([]);
  };


  // Auto-scroll para a última mensagem
  useEffect(() => {
    const messagesContainer = document.querySelector('.atendimento-messages-list');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [selectedChat?.messages]);

  // Simular mensagens automáticas do cliente
  useEffect(() => {
    if (selectedChat && selectedChat.messages.length > 0) {
      const lastMessage = selectedChat.messages[selectedChat.messages.length - 1];
      if (lastMessage.type === 'agent' && lastMessage.content.includes('Olá')) {
        // Simular resposta automática do cliente após 3 segundos
        const timer = setTimeout(() => {
          const autoResponse = {
            id: Date.now(),
            type: 'customer',
            content: 'Olá! Obrigado pelo atendimento. Como posso ser ajudado?',
            timestamp: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            status: 'delivered'
          };

          setChats(prevChats => 
            prevChats.map(chat => 
              chat.id === selectedChat.id 
                ? { 
                    ...chat, 
                    messages: [...chat.messages, autoResponse],
                    lastMessage: autoResponse.content,
                    lastMessageTime: autoResponse.timestamp
                  }
                : chat
            )
          );

          setSelectedChat(prevChat => ({
            ...prevChat,
            messages: [...prevChat.messages, autoResponse],
            lastMessage: autoResponse.content,
            lastMessageTime: autoResponse.timestamp
          }));
        }, 3000);

        return () => clearTimeout(timer);
      }
    }
  }, [selectedChat]);

  return (
    <div className="atendimento-container">
      {/* Header com estatísticas */}
      <div className="atendimento-header">
        <div className="atendimento-header-content">
          <div className="atendimento-header-title">
            <div className="atendimento-title-section">
              <h1 className="atendimento-page-title">
                <MessageCircle size={28} className="atendimento-title-icon" />
                Central de Atendimento
              </h1>
              <p className="atendimento-page-subtitle">Atendimento Omnichannel - Município de Duque de Caxias</p>
            </div>
            <div className="atendimento-header-actions">
              <button 
                className="atendimento-header-btn atendimento-primary"
                onClick={() => setShowNewChatModal(true)}
              >
                <Plus size={20} />
                Nova Conversa
              </button>
            </div>
          </div>
          
          <div className="atendimento-stats-bar">
              <div className="atendimento-stat-item">
                <div className="atendimento-stat-icon atendimento-online">
                  <Users size={16} />
                </div>
                <div className="atendimento-stat-content">
                  <span className="atendimento-stat-value">{activeAgents}</span>
                  <span className="atendimento-stat-label">Agentes Ativos</span>
                </div>
              </div>
              <div className="atendimento-stat-item">
                <div className="atendimento-stat-icon atendimento-warning">
                  <Clock size={16} />
                </div>
                <div className="atendimento-stat-content">
                  <span className="atendimento-stat-value">{queueLength}</span>
                  <span className="atendimento-stat-label">Na Fila</span>
                </div>
              </div>
              <div className="atendimento-stat-item">
                <div className="atendimento-stat-icon atendimento-success">
                  <Activity size={16} />
                </div>
                <div className="atendimento-stat-content">
                  <span className="atendimento-stat-value">{avgResponseTime}</span>
                  <span className="atendimento-stat-label">Tempo Médio</span>
                </div>
              </div>
              <div className="atendimento-stat-item">
                <div className="atendimento-stat-icon atendimento-info">
                  <TrendingUp size={16} />
                </div>
                <div className="atendimento-stat-content">
                  <span className="atendimento-stat-value">{satisfaction}</span>
                  <span className="atendimento-stat-label">Satisfação</span>
                </div>
              </div>
            </div>
        </div>
      </div>

      <div className="atendimento-layout">
        {/* Sidebar com lista de chats */}
        <div className="atendimento-chats-sidebar">
          <div className="atendimento-sidebar-header">
            <div className="atendimento-search-container">
              <Search size={20} className="atendimento-search-icon" />
              <input
                type="text"
                placeholder="Buscar conversas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="atendimento-search-input"
              />
            </div>
            
            <div className="atendimento-filter-container">
              <Filter size={16} />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="atendimento-filter-select"
              >
                <option value="todos">Todos ({chats.length})</option>
                <option value="falta_responder">Falta responder ({awaitingReplyCount})</option>
                <option value="ativo">Ativo ({chats.filter(c => c.status === 'ativo').length})</option>
                <option value="pendente">Pendente ({chats.filter(c => c.status === 'pendente').length})</option>
                <option value="resolvido">Resolvido ({chats.filter(c => c.status === 'resolvido').length})</option>
              </select>
            </div>
          </div>

          <div className="atendimento-chats-list">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Carregando chats...</p>
              </div>
            ) : filteredChats.length === 0 ? (
              <div className="atendimento-empty-state">
                <MessageCircle size={48} />
                <p>Nenhum chat encontrado</p>
                <span>Os chats aparecerão aqui quando houver conversas ativas</span>
              </div>
            ) : (
              (filteredChats || []).map(chat => {
              const unreadCount = chat.unread_count || 0;
              const hasUnread = unreadCount > 0;
              return (
              <div
                key={chat.id}
                className={`atendimento-chat-item ${selectedChat?.id === chat.id ? 'atendimento-chat-item-selected' : ''} ${hasUnread ? 'atendimento-chat-item-unread' : ''}`}
                onClick={() => handleSelectChat(chat)}
              >
                <div className="atendimento-chat-avatar">
                  <div className={`atendimento-avatar atendimento-avatar-online`}>
                    {(chat.customer_name || (chat.customer && chat.customer.name) || 'C').charAt(0).toUpperCase()}
                  </div>
                  <div className="atendimento-channel-icon">
                    {getChannelIcon(chat.channel)}
                  </div>
                </div>
                
                <div className="atendimento-chat-info">
                  <div className="atendimento-chat-header">
                    <h4 className="atendimento-customer-name">{chat.customer_name || (chat.customer && chat.customer.name) || 'Cliente'}</h4>
                    <span className={`atendimento-chat-time ${hasUnread ? 'atendimento-chat-time-unread' : ''}`}>
                      {chat.last_message_time || chat.lastMessageTime}
                    </span>
                    {(user?.is_admin || user?.is_group_admin) && (
                      <button
                        className="atendimento-spy-btn"
                        title="Espiar conversa (somente leitura)"
                        onClick={(e) => { e.stopPropagation(); handleSpyChat(chat); }}
                      >
                        <Eye size={16} />
                      </button>
                    )}
                  </div>
                  
                  <div className="atendimento-chat-meta">
                    <span className={`atendimento-status-badge ${getStatusColor(chat.status)}`}>
                      {chat.status}
                    </span>
                    <span className={`atendimento-priority-badge ${getPriorityColor(chat.priority)}`}>
                      {chat.priority}
                    </span>
                  
                    {chat.agent && (
                      <span className="atendimento-agent-badge">
                        <User size={12} />
                        {chat.agent}
                      </span>
                    )}
                    {chat.grupo && (
                      <span className="atendimento-group-badge">
                        <Users size={12} />
                        {chat.grupo}
                      </span>
                    )}
                  </div>
                  
                  <div className="atendimento-last-message-row">
                    <p className={`atendimento-last-message ${hasUnread ? 'atendimento-last-message-unread' : ''}`}>
                      {chat.last_message}
                    </p>
                    {hasUnread && (
                      <span className="atendimento-unread-badge" title={`${unreadCount} não lida(s)`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </div>
                  
                  <div className="atendimento-chat-footer">
                    <div className="atendimento-chat-tags">
                      <span className="atendimento-tag">{chat.protocolo}</span>
                      <span className="atendimento-tag">{chat.channel}</span>
                    </div>
                    <div className="atendimento-wait-time">
                      <Clock size={12} />
                      {chat.waitTime}
                    </div>
                  </div>
                </div>
              </div>
              );
              })
            )}
          </div>
        </div>

        {/* Área principal do chat */}
        <div 
          className={`atendimento-chat-main ${isDragging ? 'atendimento-drag-over' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isDragging && (
            <div className="atendimento-drag-overlay">
              <div className="atendimento-drag-content">
                <Paperclip size={48} />
                <h3>Solte os arquivos aqui</h3>
                <p>Arraste e solte arquivos para anexar</p>
              </div>
            </div>
          )}
          {selectedChat && selectedChat.id ? (
            <>
              {/* Header do chat */}
              <div className="atendimento-chat-header">
                <div className="atendimento-chat-customer-info">
                <div className="atendimento-customer-avatar">
                 <div className="atendimento-avatar atendimento-avatar-online">
                    {(selectedChat.customer_name || (selectedChat.customer && selectedChat.customer.name) || 'C').charAt(0).toUpperCase()}
                    </div>
                    <div className="atendimento-customer-details">
                      <h3>{selectedChat.customer_name || (selectedChat.customer && selectedChat.customer.name) || 'Cliente'}</h3>
                      <div className="atendimento-customer-meta">
                        <span className="atendimento-customer-status atendimento-customer-status-online">
                          <span className="atendimento-status-dot"></span>
                          Online
                        </span>
                        <div className="atendimento-meta-details">
                          <span className="atendimento-last-seen">Protocolo: {selectedChat.protocolo}</span>
                          {selectedChat.agent && (
                            <span className="atendimento-agent-info">
                              {selectedChat.agent}
                            </span>
                          )}
                          {selectedChat.grupo && (
                            <span className="atendimento-group-info">
                              <Users size={14} />
                              {selectedChat.grupo}
                            </span>
                          )}
                          <span><Phone size={16} /> {selectedChat.phone || (selectedChat.customer && selectedChat.customer.phone) || 'Não informado'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="atendimento-customer-contact">
                   
                    
                   
                  </div>
                </div>
                
                <div className="atendimento-chat-actions-container">
                <div className="atendimento-chat-actions">
                  <div className="atendimento-action-group">
                    <button 
                      className="atendimento-action-btn"
                      onClick={() => alert('🚧 FUNCIONALIDADE EM DESENVOLVIMENTO 🚧\n\nA funcionalidade "Ligar" está sendo desenvolvida.\nEm breve estará disponível!')}
                      title="Ligar"
                    >
                      <PhoneCall size={20} />
                    </button>
                    <button 
                      className="atendimento-action-btn"
                      onClick={() => alert('🚧 FUNCIONALIDADE EM DESENVOLVIMENTO 🚧\n\nA funcionalidade "Vídeo Chamada" está sendo desenvolvida.\nEm breve estará disponível!')}
                      title="Vídeo Chamada"
                    >
                      <Video size={20} />
                    </button>
                    <button 
                      className="atendimento-action-btn"
                      onClick={() => alert('🚧 FUNCIONALIDADE EM DESENVOLVIMENTO 🚧\n\nA funcionalidade "Favoritar" está sendo desenvolvida.\nEm breve estará disponível!')}
                      title="Favoritar"
                    >
                      <Star size={20} />
                    </button>
                  </div>
                  
                  {/* Botões de gerenciamento de agente */}
                  {selectedChat.agent && (
                    <>
                      <button 
                        className="atendimento-action-btn atendimento-transfer-btn"
                        onClick={handleOpenTransferModal}
                        title="Transferir Chat"
                      >
                        <ArrowRightLeft size={20} />
                      </button>
                      <button 
                        className="atendimento-action-btn atendimento-remove-btn"
                        onClick={handleOpenRemoveModal}
                        title="Colocar na Fila"
                      >
                        <UserX size={20} />
                      </button>
                    </>
                  )}
                  
                  <button 
                    className="atendimento-action-btn"
                    onClick={() => handleFinalizarChat()}
                    title="Finalizar Chat"
                  >
                    <Archive size={20} />
                  </button>
                  <button 
                    className="atendimento-action-btn"
                    onClick={refreshMessages}
                    title="Atualizar Mensagens"
                    disabled={loadingMessages}
                  >
                    <RefreshCw size={20} className={loadingMessages ? 'animate-spin' : ''} />
                  </button>
                </div>
                {isSpying && (user?.is_admin || user?.is_group_admin) && (
                  <div className="atendimento-spy-mode-indicator" style={{marginLeft: 'auto'}}>
                    <Eye size={16} />
                    <span>Visualizando (somente leitura)</span>
                    <button className="atendimento-exit-spy-btn" onClick={exitSpy} title="Sair da visualização" style={{marginLeft: 8}}>
                      <XCircle size={16} />
                    </button>
                  </div>
                )}
                </div>
               
              </div>
             

              {/* Mensagens */}
              <div className="atendimento-messages-container">
                {loadingMessages ? (
                  <div className="loading-messages">
                    <div className="loading-spinner"></div>
                    <p>Carregando mensagens...</p>
                  </div>
                ) : (
                  <div className="atendimento-messages-list">
                    {(selectedChat.messages || []).map(message => (
                    <div key={message.id} className={`atendimento-message ${message.type === 'customer' ? 'atendimento-message-customer' : message.type === 'agent' ? 'atendimento-message-agent' : ''}`}>
                      <div className="atendimento-message-content">
                        {message.isAudio ? (
                          <div className="atendimento-audio-message">
                            <div className="atendimento-audio-player">
                              <button 
                                className="atendimento-play-button"
                                onClick={() => playAudio(message.audioUrl, message.id)}
                              >
                                {playingAudio === message.id ? (
                                  <Pause size={16} />
                                ) : (
                                  <Play size={16} />
                                )}
                              </button>
                              <div className="atendimento-audio-info">
                                <span className="atendimento-audio-duration">
                                  {message.duration ? formatDuration(message.duration) : '0:15'}
                                </span>
                                <div className="atendimento-audio-waveform">
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : message.isFile ? (
                          <div className="atendimento-attachments">
                            {(message.files || []).map((file, idx) => (
                              <React.Fragment key={`${message.id}-file-${idx}`}>
                                {renderFileCard({
                                  url: file.url,
                                  filename: file.name,
                                  size: file.size,
                                  mimeType: file.type
                                })}
                              </React.Fragment>
                            ))}
                          </div>
                        ) : isAudioUrl(message.content) ? (
                          <div className="atendimento-audio-message">
                            <div className="atendimento-audio-player">
                                <button 
                                  className="atendimento-play-button"
                                  onClick={() => playAudio(fixFileUrl(extractAudioUrl(message.content)), message.id)}
                                >
                                {playingAudio === message.id ? (
                                  <Pause size={16} />
                                ) : (
                                  <Play size={16} />
                                )}
                              </button>
                              <div className="atendimento-audio-info">
                                <span className="atendimento-audio-duration">
                                  {message.duration ? formatDuration(message.duration) : '0:15'}
                                </span>
                                <div className="atendimento-audio-waveform">
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                  <div className={`atendimento-wave-bar ${playingAudio === message.id ? 'atendimento-wave-bar-active' : ''}`}></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : isAttachmentUrl(message.content) ? (
                          (() => {
                            const attachment = parseAttachmentContent(message.content);
                            if (!attachment) return null;
                            return (
                              <div className="atendimento-attachments">
                                {renderFileCard({
                                  url: attachment.url,
                                  filename: attachment.filename
                                })}
                                {attachment.caption ? (
                                  <div
                                    className="atendimento-message-text atendimento-attachment-caption"
                                    dangerouslySetInnerHTML={{ __html: formatMessageText(attachment.caption) }}
                                  />
                                ) : null}
                              </div>
                            );
                          })()
                        ) : (
                          <div 
                            className="atendimento-message-text"
                            dangerouslySetInnerHTML={{ __html: formatMessageText(message.content) }}
                          />
                        )}
                        <div className="atendimento-message-meta">
                          <span className="atendimento-message-time">{message.timestamp}</span>
                          {message.type === 'agent' && (
                            <span className="atendimento-message-status">
                              {message.status === 'sent' && <CheckCircle size={14} />}
                              {message.status === 'delivered' && <CheckCircle size={14} />}
                              {message.status === 'read' && <CheckCircle size={14} className="atendimento-read" />}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  </div>
                )}
              </div>

              {/* Input de mensagem */}
              <div className="atendimento-message-input-container">
                {/* Área de arquivos selecionados */}
                {selectedFiles.length > 0 && (
                  <div className="atendimento-selected-files">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="atendimento-file-preview">
                        <FileText size={16} />
                        <span className="atendimento-file-name">{file.name}</span>
                        <button 
                          className="atendimento-remove-file-btn"
                          onClick={() => removeFile(index)}
                        >
                          <XCircle size={14} />
                        </button>
                      </div>
                    ))}
                    <button 
                      className="atendimento-send-files-btn"
                      onClick={sendFiles}
                    >
                      <Send size={16} />
                      Enviar {selectedFiles.length} arquivo(s)
                    </button>
                  </div>
                )}

                {/* Área de áudio gravado */}
                {audioBlob && (
                  <div className="atendimento-audio-preview">
                    <div className="atendimento-audio-preview-content">
                      <Mic size={16} />
                      <span>Áudio gravado ({recordingTime}s)</span>
                    </div>
                    <div className="atendimento-audio-preview-actions">
                      <button 
                        className="atendimento-cancel-audio-btn"
                        onClick={() => setAudioBlob(null)}
                      >
                        <XCircle size={16} />
                      </button>
                      <button 
                        className="atendimento-send-audio-btn"
                        onClick={sendAudio}
                      >
                        <Send size={16} />
                        Enviar
                      </button>
                    </div>
                  </div>
                )}

                <div className="atendimento-input-actions">
                  <div className="atendimento-file-input-wrapper">
                    <input
                      type="file"
                      id="file-input"
                      multiple
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <button 
                      className="atendimento-input-btn"
                      onClick={() => document.getElementById('file-input').click()}
                    >
                      <Paperclip size={20} />
                    </button>
                  </div>
                  
                  <div className="atendimento-emoji-picker-wrapper">
                    <button 
                      className="atendimento-input-btn"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    >
                      <Smile size={20} />
                    </button>
                    
                    {showEmojiPicker && (
                      <div className="atendimento-emoji-picker">
                        <div className="atendimento-emoji-grid">
                          {['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻', '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'].map(emoji => (
                            <button
                              key={emoji}
                              className="atendimento-emoji-btn"
                              onClick={() => addEmoji(emoji)}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="atendimento-message-input-wrapper">
                  {chatBloqueado && (
                    <div className="atendimento-chat-bloqueado-warning">
                      <AlertCircle size={16} />
                      <span>Chat bloqueado - Aguardando confirmação do cliente</span>
                    </div>
                  )}
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      isSpying 
                        ? "Visualização ativa — envio desabilitado" 
                        : chatBloqueado 
                        ? "⏳ Chat bloqueado - Aguarde a confirmação do cliente..." 
                        : "Digite sua mensagem..."
                    }
                    className={`atendimento-message-input ${chatBloqueado ? 'atendimento-input-bloqueado' : ''}`}
                    rows={1}
                    readOnly={isSpying || chatBloqueado}
                  />
                </div>
                
                <div className="atendimento-send-actions">
                  {!isSpying && (
                    <MensagensRapidas
                      onSelectMessage={(mensagem) => setNewMessage(mensagem)}
                      chatInfo={selectedChat}
                    />
                  )}
                  
                  <button 
                    className={`atendimento-input-btn ${isRecording ? 'atendimento-input-btn-recording' : ''}`}
                    onClick={isRecording ? stopRecording : startRecording}
                  >
                    <Mic size={20} />
                    {isRecording && <span className="atendimento-recording-dot"></span>}
                  </button>
                  
                  {isRecording && (
                    <div className="atendimento-recording-timer">
                      {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                    </div>
                  )}
                  
                  <button 
                    className="atendimento-send-btn"
                    onClick={sendMessage}
                    disabled={isSpying || chatBloqueado || (!newMessage.trim() && selectedFiles.length === 0 && !audioBlob)}
                    title={chatBloqueado ? "Chat bloqueado - Aguarde a confirmação do cliente" : "Enviar mensagem"}
                  >
                    <Send size={20} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="atendimento-no-chat-selected">
              <div className="atendimento-no-chat-content">
                <MessageCircle size={80} className="atendimento-no-chat-icon" />
                <h3>Selecione uma conversa</h3>
                <p>Escolha uma conversa da lista para começar o atendimento</p>
                <div className="atendimento-quick-stats">
                  <div className="atendimento-quick-stat">
                    <Users size={20} />
                    <span>{activeAgents} agentes online</span>
                  </div>
                  <div className="atendimento-quick-stat">
                    <Clock size={20} />
                    <span>{queueLength} na fila</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar de informações do cliente */}
        {selectedChat && (
          <div className="atendimento-customer-sidebar">
            <div className="atendimento-sidebar-header">
              <h3>Informações do Cliente</h3>
              <button className="atendimento-sidebar-toggle">
                <Minimize2 size={16} />
              </button>
            </div>

            <div className="atendimento-sidebar-section">
              <h4>Dados Pessoais</h4>
              <div className="atendimento-info-grid">
                <div className="atendimento-info-item">
                  <User size={16} />
                  <div className="atendimento-info-content">
                    <span className="atendimento-info-label">Nome</span>
                    <span className="atendimento-info-value">{selectedChat.customer.name}</span>
                  </div>
                </div>
                <div className="atendimento-info-item">
                  <Phone size={16} />
                  <div className="atendimento-info-content">
                    <span className="atendimento-info-label">Telefone</span>
                    <span className="atendimento-info-value">{selectedChat.customer.phone}</span>
                  </div>
                </div>
                <div className="atendimento-info-item">
                  <Mail size={16} />
                  <div className="atendimento-info-content">
                    <span className="atendimento-info-label">Email</span>
                    <span className="atendimento-info-value">{selectedChat.customer.email}</span>
                  </div>
                </div>
                <div className="atendimento-info-item">
                  <MapPin size={16} />
                  <div className="atendimento-info-content">
                    <span className="atendimento-info-label">Localização</span>
                    <span className="atendimento-info-value">{selectedChat.customer.location}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="atendimento-sidebar-section">
              <h4>Histórico do Cliente</h4>
              <div className="atendimento-history-stats">
                <div className="atendimento-history-item">
                  <Calendar size={16} />
                  <div className="atendimento-history-content">
                    <span className="atendimento-history-label">Cliente desde</span>
                    <span className="atendimento-history-value">{new Date(selectedChat.customer.customerSince).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
                <div className="atendimento-history-item">
                  <FileText size={16} />
                  <div className="atendimento-history-content">
                    <span className="atendimento-history-label">Total de tickets</span>
                    <span className="atendimento-history-value">{selectedChat.customer.totalTickets}</span>
                  </div>
                </div>
                <div className="atendimento-history-item">
                  <Award size={16} />
                  <div className="atendimento-history-content">
                    <span className="atendimento-history-label">Satisfação</span>
                    <span className="atendimento-history-value">{selectedChat.customer.satisfaction}/5.0</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="atendimento-sidebar-section">
              <h4>Status da Conversa</h4>
              <div className="status-grid">
                <div className="status-item">
                  <span className="status-label">Status:</span>
                  <span className={`atendimento-status-badge ${getStatusColor(selectedChat.status)}`}>
                    {selectedChat.status}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Prioridade:</span>
                  <span className={`atendimento-priority-badge ${getPriorityColor(selectedChat.priority)}`}>
                    {selectedChat.priority}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Canal:</span>
                  <span className="atendimento-channel-info">
                    {getChannelIcon(selectedChat.channel)}
                    {selectedChat.channel}
                  </span>
                </div>
                {selectedChat.assignedAgent && (
                  <div className="status-item">
                    <span className="status-label">Agente:</span>
                    <span className="atendimento-agent-info">
                      <User size={14} />
                      {selectedChat.assignedAgent}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="atendimento-sidebar-section">
              <h4>Tags</h4>
              <div className="atendimento-tags-container">
                {(selectedChat.tags || []).map(tag => (
                  <span key={tag} className="atendimento-tag">{tag}</span>
                ))}
                <button className="atendimento-add-tag-btn">
                  <Plus size={14} />
                  Adicionar
                </button>
              </div>
            </div>

            <div className="atendimento-sidebar-section">
              <h4>Ações Rápidas</h4>
              <div className="atendimento-quick-actions">
                <button 
                  className="atendimento-quick-action-btn"
                  onClick={() => alert('🚧 FUNCIONALIDADE EM DESENVOLVIMENTO 🚧\n\nA funcionalidade "Arquivar" está sendo desenvolvida.\nEm breve estará disponível!')}
                >
                  <Archive size={16} />
                  Arquivar
                </button>
                <button 
                  className="atendimento-quick-action-btn"
                  onClick={() => alert('🚧 FUNCIONALIDADE EM DESENVOLVIMENTO 🚧\n\nA funcionalidade "Favoritar" está sendo desenvolvida.\nEm breve estará disponível!')}
                >
                  <Star size={16} />
                  Favoritar
                </button>
                <button 
                  className="atendimento-quick-action-btn"
                  onClick={() => alert('🚧 FUNCIONALIDADE EM DESENVOLVIMENTO 🚧\n\nA funcionalidade "Exportar" está sendo desenvolvida.\nEm breve estará disponível!')}
                >
                  <Download size={16} />
                  Exportar
                </button>
                <button 
                  className="atendimento-quick-action-btn"
                  onClick={() => alert('🚧 FUNCIONALIDADE EM DESENVOLVIMENTO 🚧\n\nA funcionalidade "Excluir" está sendo desenvolvida.\nEm breve estará disponível!')}
                >
                  <Trash2 size={16} />
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Nova Conversa */}
      {showNewChatModal && (
        <div className="atendimento-modal-overlay">
          <div className="atendimento-modal-content">
            <div className="atendimento-modal-header">
              <h3 className="atendimento-modal-title">Nova Conversa</h3>
              <button 
                className="atendimento-modal-close"
                onClick={() => setShowNewChatModal(false)}
              >
                <XCircle size={20} />
              </button>
            </div>
            
            <div className="atendimento-modal-body">
              <div className="atendimento-form-group">
                <label className="atendimento-form-label">Nome do Cliente *</label>
                <input
                  type="text"
                  className="atendimento-form-input"
                  value={newChatData.customerName}
                  onChange={(e) => handleNewChatInputChange('customerName', e.target.value)}
                  placeholder="Digite o nome do cliente"
                  autoFocus
                />
              </div>
              
              <div className="atendimento-form-group">
                <label className="atendimento-form-label">Telefone *</label>
                <input
                  type="text"
                  className="atendimento-form-input"
                  value={newChatData.phone}
                  onChange={(e) => handleNewChatInputChange('phone', e.target.value)}
                  placeholder="21999999999 ou 5521999999999"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newChatData.customerName.trim() && newChatData.phone.trim()) {
                      createNewChat();
                    }
                  }}
                />
              </div>
            </div>
            
            <div className="atendimento-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowNewChatModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary"
                onClick={createNewChat}
              >
                <Plus size={18} />
                Criar Conversa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Transferir Chat */}
      {showTransferModal && (
        <div className="atendimento-modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="atendimento-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Transferir Chat</h3>
            <p>Selecione o novo agente para transferir este chat:</p>
            <div className="atendimento-form-group">
              <select
                className="atendimento-form-input"
                value={novoAgente}
                onChange={(e) => setNovoAgente(e.target.value)}
                autoFocus
              >
                <option value="">Selecione um agente...</option>
                {usuariosDisponiveis.map(usuario => (
                  <option key={usuario.id} value={usuario.full_name || usuario.username}>
                    {usuario.full_name || usuario.username} {usuario.is_admin ? '(Admin)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="atendimento-modal-actions">
              <button
                className="atendimento-cancel-button"
                onClick={() => {
                  setShowTransferModal(false);
                  setNovoAgente('');
                }}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleTransferChat}
                disabled={!novoAgente.trim()}
              >
                <ArrowRightLeft size={16} />
                Transferir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Remover Agente */}
      {showRemoveModal && (
        <div className="atendimento-modal-overlay" onClick={() => setShowRemoveModal(false)}>
          <div className="atendimento-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Colocar na Fila</h3>
            <p>Tem certeza que deseja remover o agente deste chat? O chat será colocado na fila para outro agente.</p>
            
            <div className="atendimento-form-group">
              <label className="atendimento-form-label">
                Selecionar Grupo:
              </label>
              <select
                className="atendimento-form-input"
                value={grupoSelecionado}
                onChange={(e) => setGrupoSelecionado(e.target.value)}
              >
                {gruposDisponiveis.map(grupo => (
                  <option key={grupo.id} value={grupo.name}>
                    {grupo.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="atendimento-modal-actions">
              <button
                className="atendimento-cancel-button"
                onClick={() => {
                  setShowRemoveModal(false);
                  setGrupoSelecionado('');
                }}
              >
                Cancelar
              </button>
              <button
                className="atendimento-remove-button"
                onClick={handleRemoveAgent}
              >
                <UserX size={16} />
                Colocar na Fila
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Atendimento;
